import { usePrefs } from '../hooks/usePrefs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Category, Household, Item, Member } from '../lib/supabase'
import { basketOf, progress, staleBasket } from '../lib/items'
import { computeRestock, type RestockChip } from '../lib/restock'
import type { CatalogEntry } from '../lib/catalog'
import { tintVar } from '../lib/categoryTint'
import { HAPTICS } from '../lib/constants'
import { haptic } from '../lib/haptics'
import { LAYOUT_SPEC } from '../lib/layout'
import { useLayout } from '../hooks/useLayout'
import { useSwipeRows } from '../hooks/useSwipeRows'
import { useUndoDelete } from '../hooks/useUndoDelete'
import { itemHistoryKey, useItemHistory } from '../hooks/useItemHistory'
import { ItemEditor } from './list/ItemEditor'
import type { FinishedTrip } from './list/TripSummarySheet'
import { TripSheets } from './stats/TripSheets'
import { AllItemsSheet } from './list/AllItemsSheet'
import { userLocale } from '../lib/locale'
import { ShoppingMode } from './shopping/ShoppingMode'
import { EndTripDialog } from './shopping/EndTripDialog'
import { StorePicker } from './shopping/StorePicker'
import { useShopping } from '../hooks/useShopping'
import { routeOrder } from '../lib/storeRoute'
import { tripRecap } from '../lib/trips'
import { endTripText, memberName, shopLabel } from '../lib/sharedTrip'
import { ShopView } from './list/ShopView'
import { PlanView } from './list/PlanView'
import { ListReadyProvider } from './list/ListReadyProvider'
import { useLatest } from '../hooks/useLatest'
import { freeItemsLeft, hasPlus, planCaption, type PlusReason } from '../lib/billing'

// The list screen: the data and every flow (basket, delete + Undo, restock,
// editor, sheets) live here once; ShopView (phone) and PlanView (desktop and
// iPad) are two bodies for the same state (ux/01 "Two bodies, two jobs").


interface Props {
  household: Household
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  isLoading: boolean
  onToggle: (id: string, done: boolean) => void
  /** Returns the new row's id (for Undo), or nothing when the add was refused. */
  onAdd: (name: string, category_id?: string | null, quantity?: string | null, note?: string | null) => string | void
  onUpdate: (id: string, name: string, quantity: string | null, category_id: string | null, restore?: boolean, note?: string | null) => void
  onDelete: (id: string) => void
  onRestore: (item: Item, index: number) => void
  /** Finish trip: the basket leaves the screen while `write` finishes it on the server (useItems.finishBasket). */
  onFinishBasket: (write: () => Promise<unknown>) => Promise<Item[]>
  /** Takes these rows off the list as finished (useItems.finishItems), without asking. */
  onFinishStale: (ids: string[]) => Promise<Item[]>
  /** Adds a full free list refused (useItems.unsaved): shown, never dropped. */
  unsaved: Item[]
  onDiscardUnsaved: (id: string) => void
  /** Writes the unsaved adds again (useItems.retryUnsaved). */
  onRetryUnsaved: () => void
  /** Opens the subscribe sheet, leading with what the tap was after (if anything in particular). */
  onUpgrade: (reason?: PlusReason) => void
  /** Home's Start shopping: start (or carry on) shopping once the list is open, then onShopRequested. */
  shopRequested?: boolean
  onShopRequested?: () => void
  /** Home's End trip: ask about someone else's trip once the list is open, then onEndRequested. */
  endRequested?: boolean
  onEndRequested?: () => void
}

/** What both bodies render from. */
export interface ListModel {
  household: Household
  items: Item[]
  categories: Category[]
  members: Member[]
  selfMemberId: string | null
  isLoading: boolean
  /** Trial, paid or grace (lib/billing.ts). Without it the list is Free: up to 15 open items. */
  plus: boolean
  /** Places left on a free list; Infinity with Plus. */
  itemsLeft: number
  /** Adds a full free list refused, shown as not saved. */
  unsaved: Item[]
  onDiscardUnsaved: (id: string) => void
  /** The one line under the hero about the plan, or null while paid. */
  planCaption: string | null
  active: Item[]
  basket: Item[]
  trip: { inBasket: number; total: number }
  chips: RestockChip[]
  /** Names used before and not on the list, for quick add (lib/catalog.ts). */
  catalog: CatalogEntry[]
  /** nameKey → the category this household files it under. */
  categoryMemory: Map<string, string>
  tintFor: (categoryId: string | null) => string
  swipe: ReturnType<typeof useSwipeRows>
  undo: ReturnType<typeof useUndoDelete>
  announce: (message: string) => void
  onAdd: (name: string, categoryId: string | null) => void
  onAddFromBefore: (entry: CatalogEntry) => void
  onOpenFromBefore: (entry: CatalogEntry) => void
  /** Quick add's grid button: All items (AllItemsSheet). */
  onBrowse: () => void
  onPutBack: (id: string) => void
  onEdit: (id: string, intent?: 'add' | 'edit') => void
  onDelete: (id: string, viaKeyboard?: boolean) => void
  /** Phone: opens shopping mode (after the store picker, unless a trip is open; joining someone else's). */
  onStartShopping: () => void
  /** The button's words: Start shopping, Continue shopping, or Join Anna (lib/sharedTrip.ts). */
  shopLabel: string
  /** Someone else's trip is open: End trip, for everyone, after a question (EndTripDialog). */
  onEndTrip?: () => void
  onChip: (chip: RestockChip) => void
  onUpgrade: (reason?: PlusReason) => void
}

export function GroceryList(props: Props) {
  const {
    household, items: allItems, categories, members, selfMemberId, isLoading,
    onToggle, onAdd, onUpdate, onDelete, onRestore, onFinishBasket, onFinishStale,
    unsaved, onDiscardUnsaved, onRetryUnsaved, onUpgrade,
  } = props
  const plus = hasPlus(household)
  const layout = useLayout()
  const spec = LAYOUT_SPEC[layout]
  const mode = spec.mode
  const qc = useQueryClient()

  const [editing, setEditing] = useState<{ id: string; intent: 'add' | 'edit' } | null>(null)
  // A name from "From before" opened in the editor: not a row yet, added on "Add".
  const [draft, setDraft] = useState<CatalogEntry | null>(null)
  const [browsing, setBrowsing] = useState(false)
  const [finished, setFinished] = useState<FinishedTrip | null>(null)
  // Shopping mode (phone): open, and the store picker before it or over it.
  const [shoppingOpen, setShoppingOpen] = useState(false)
  const [picker, setPicker] = useState<'start' | 'change' | null>(null)
  // End trip's question, and when it was asked (its "last tick 2 h ago").
  const [endAsking, setEndAsking] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const undoRef = useRef<HTMLButtonElement>(null)
  // Shopping mode's toast: mounted over the list's own while shopping.
  const shopUndoRef = useRef<HTMLButtonElement>(null)

  // A basket left from an earlier trip (lib/items.ts staleBasket) is history,
  // not today's basket: it is left out at once and finished in the background.
  // `now` is when the list was opened or last came back to the foreground —
  // an installed PWA resumes the next morning without remounting.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setNow(Date.now()) }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
  const stale = useMemo(() => staleBasket(allItems, new Date(now)), [allItems, now])
  const items = useMemo(() => {
    if (!stale.length) return allItems
    const gone = new Set(stale.map(i => i.id))
    return allItems.filter(i => !gone.has(i.id))
  }, [allItems, stale])
  const finishing = useRef(new Set<string>())
  useEffect(() => {
    const ids = stale.map(i => i.id).filter(id => !finishing.current.has(id))
    if (!ids.length) return
    ids.forEach(id => finishing.current.add(id))
    // Housekeeping: nothing to announce, and a refusal leaves the rows hidden.
    onFinishStale(ids).then(
      () => qc.invalidateQueries({ queryKey: itemHistoryKey(household.id) }),
      () => {},
    )
  }, [stale, onFinishStale, qc, household.id])

  const { purchases: history, memory: categoryMemory, catalog, trips } = useItemHistory(household.id, items)

  const byId = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const tintFor = useCallback((categoryId: string | null) => tintVar(categoryId ? byId.get(categoryId) : null), [byId])
  const active = useMemo(() => items.filter(i => !i.done), [items])
  // Free holds 15 open items (042_free_plus.sql). What the screen can see, it
  // stops here — the server's trigger refuses the rest, and useItems keeps
  // those adds as unsaved rows rather than dropping them.
  const itemsLeft = freeItemsLeft(household, active.length)
  const itemsLeftRef = useLatest(itemsLeft)
  // Upgraded (on this phone or another): the adds the full list refused go in —
  // once per arrival of Plus. The phone's clock can say Plus a moment before
  // the server does; a refused retry then comes back unsaved and stays there,
  // rather than being sent again in a loop.
  const unsavedRef = useLatest(unsaved)
  const retryRef = useLatest(onRetryUnsaved)
  useEffect(() => {
    if (plus && unsavedRef.current.length) retryRef.current()
  }, [plus, unsavedRef, retryRef])
  const basket = useMemo(() => basketOf(items), [items])
  const trip = progress(items)

  const announce = useCallback((m: string) => setMessage(m), [])

  const undo = useUndoDelete(items, onDelete, onRestore)
  const itemsRef = useLatest(items)

  const toBasket = useCallback((id: string) => {
    const item = itemsRef.current.find(i => i.id === id)
    if (!item) return
    onToggle(id, true)
    const t = progress(itemsRef.current)
    announce(`${item.name} in basket. ${t.inBasket + 1} of ${t.total}.`)
  }, [onToggle, announce, itemsRef])

  // Putting a ticked item back makes it open again: a full free list offers Plus instead.
  const putBack = useCallback((id: string) => {
    if (itemsLeftRef.current <= 0) { onUpgrade('list_full'); return }
    onToggle(id, false)
  }, [onToggle, onUpgrade, itemsLeftRef])

  const deleteItem = useCallback((id: string, viaKeyboard = false) => {
    haptic(HAPTICS.remove)
    undo.deleteWithUndo(id)
    // Keyboard path: the toast's Undo takes focus once it has rendered.
    if (viaKeyboard) setTimeout(() => (shopUndoRef.current ?? undoRef.current)?.focus(), 50)
  }, [undo])

  const openEditor = useCallback((id: string, intent: 'add' | 'edit' = 'edit') => setEditing({ id, intent }), [])

  // The list's rows: swipe left deletes. Swiping right into the basket is shopping mode's.
  const swipe = useSwipeRows({
    swipeRight: false,
    onBasket: toBasket,
    onDelete: id => deleteItem(id),
    onEdit: id => openEditor(id),
  })
  const { close: closeRows } = swipe

  // Shopping mode's own rows: there swipe right is the basket.
  const shopSwipe = useSwipeRows({
    swipeRight: true,
    onBasket: toBasket,
    onDelete: id => deleteItem(id),
    onEdit: id => openEditor(id),
  })
  const shopping = useShopping(household.id, selfMemberId, { plus })
  const store = shopping.storeOf(shopping.trip?.storeId ?? null)
  const ordered = useMemo(
    () => (shoppingOpen ? routeOrder(active, store?.route ?? [], categories, categoryMemory, userLocale()) : []),
    [shoppingOpen, active, store, categories, categoryMemory],
  )
  // This person's trip carries on; someone else's is joined (045); otherwise,
  // where are you?
  const { role: tripRole, join: joinTrip } = shopping
  const startShopping = useCallback(() => {
    if (tripRole === 'join') joinTrip()
    if (tripRole !== 'none') setShoppingOpen(true)
    else setPicker('start')
  }, [tripRole, joinTrip])
  // The trip ended elsewhere (someone finished it, or this person on their
  // watch) while shopping mode was open: it closes; App says what happened.
  if (shoppingOpen && shopping.loaded && tripRole !== 'mine') setShoppingOpen(false)
  // Home's Start shopping lands here: once this phone's trips are read, carry
  // on the open one or ask where.
  const { shopRequested, onShopRequested } = props
  useEffect(() => {
    if (shopRequested && shopping.loaded) {
      onShopRequested?.()
      // A one-off request from another tab, answered once the trips are read:
      // an effect is the place for it.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      startShopping()
    }
  }, [shopRequested, onShopRequested, shopping.loaded, startShopping])
  // Home's End trip lands here too: ask once the trips are read (and only if
  // it is still someone else's trip by then).
  const { endRequested, onEndRequested } = props
  useEffect(() => {
    if (endRequested && shopping.loaded) {
      onEndRequested?.()
      // A one-off request from another tab, as Start shopping's above.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (tripRole === 'join') setEndAsking(Date.now())
    }
  }, [endRequested, onEndRequested, shopping.loaded, tripRole])
  const pickStore = (storeId: string | null) => {
    shopping.start(storeId)
    setPicker(null)
    setShoppingOpen(true)
  }

  // Opening any sheet closes an open row (ux/04).
  const sheetOpen = !!editing || !!draft
  const { close: closeShopRows } = shopSwipe
  useEffect(() => { if (sheetOpen) { closeRows(); closeShopRows() } }, [sheetOpen, closeRows, closeShopRows])

  const { prefs } = usePrefs()
  const tripsRef = useLatest(trips)
  const finishTrip = useCallback(async () => {
    haptic(HAPTICS.finishTrip)
    try {
      // The database finishes the trip and takes everyone's basket off in one
      // go (045); the basket leaves this screen at once, and comes back if not.
      let done: { tripId: string; finishedAt: string; already: boolean } | null = null
      const bought = await onFinishBasket(async () => {
        done = await shopping.finish(basket.map(i => ({ id: i.id, name: i.name, done_at: i.done_at, done_by: i.done_by })))
      })
      setShoppingOpen(false)
      const result = done as { tripId: string; finishedAt: string; already: boolean } | null
      if (result?.already) {
        // Someone finished it a moment before: theirs stands, and its summary
        // opens as a trip that ended elsewhere.
        announce('The trip was already finished.')
      } else {
        const storeName = store?.name ?? null
        announce(`Trip finished. ${bought.length} ${bought.length === 1 ? 'item' : 'items'} bought.`)
        // Measured against the trips before this one, read before the refetch adds it.
        if (bought.length && result) {
          setFinished({ recap: tripRecap(bought, tripsRef.current), finishedAt: new Date(result.finishedAt), tripId: result.tripId, storeName })
        }
      }
      qc.invalidateQueries({ queryKey: itemHistoryKey(household.id) })
    } catch {
      announce('Could not finish the trip. Your basket is unchanged.')
    }
  }, [onFinishBasket, announce, qc, household.id, tripsRef, shopping, store, basket])

  // End trip: someone else's forgotten trip, finished for everyone from here.
  // The same finish as theirs (the basket leaves, the store learns each
  // shopper's walk), but the summary and the cost aren't this person's to see:
  // Home's Last trip offers Add cost to anyone.
  const joinName = memberName(members, shopping.joinMemberId)
  const askEnd = useCallback(() => setEndAsking(Date.now()), [])
  const endTrip = useCallback(async () => {
    setEndAsking(null)
    haptic(HAPTICS.finishTrip)
    try {
      let already = false
      const bought = await onFinishBasket(async () => {
        already = (await shopping.finish(basket.map(i => ({ id: i.id, name: i.name, done_at: i.done_at, done_by: i.done_by })))).already
      })
      announce(already
        ? 'The trip was already finished.'
        : `Trip ended. ${bought.length} ${bought.length === 1 ? 'item' : 'items'} bought.`)
      qc.invalidateQueries({ queryKey: itemHistoryKey(household.id) })
    } catch {
      announce('Could not end the trip. The basket is unchanged.')
    }
  }, [onFinishBasket, announce, qc, household.id, shopping, basket])

  // Settings' "Running low suggestions" switch (this device) hides the chips;
  // Running low is Plus (it is the household's memory).
  const chips = useMemo(() => {
    if (!prefs.restock || !plus) return []
    const onList = items.map(i => i.name)
    return computeRestock(history, onList, new Date())
  }, [history, items, prefs.restock, plus])

  // Null once the item is gone (deleted here or on another phone): closed.
  const editingItem = editing ? items.find(i => i.id === editing.id) ?? null : null
  const draftItem = useMemo(() => (draft ? draftRow(draft, household.id) : null), [draft, household.id])

  // Every add from the list screen — typed, or from "From before" (the plus,
  // or "Add" in the editor) — offers Undo in the delete toast's place; Undo
  // soft-deletes the new row again. The toast is role="status", so it is
  // also what gets announced.
  const addWithUndo = (name: string, categoryId: string | null, quantity?: string | null, note?: string | null) => {
    if (itemsLeftRef.current <= 0) { onUpgrade('list_full'); return }
    const known = categoryId && byId.has(categoryId) ? categoryId : null
    const id = quantity === undefined ? onAdd(name, known) : onAdd(name, known, quantity, note)
    if (id) undo.addedWithUndo(id, name)
  }

  // All items: the ticked names on the list at once, each in its last
  // category (if it still exists), and one Undo that takes them all off.
  const addAll = (entries: CatalogEntry[]) => {
    const room = entries.slice(0, Math.max(0, itemsLeftRef.current))
    const ids = room
      .map(e => onAdd(e.name, e.categoryId && byId.has(e.categoryId) ? e.categoryId : null))
      .filter((id): id is string => !!id)
    setBrowsing(false)
    if (room.length < entries.length) onUpgrade('list_full')
    if (ids.length) undo.bulkWithUndo(ids.length === 1 ? `Added “${room[0].name}”` : `Added ${ids.length} items`, () => ids.forEach(id => onDelete(id)))
  }


  const model: ListModel = {
    household, items, categories, members, selfMemberId, isLoading,
    plus, itemsLeft, unsaved, onDiscardUnsaved, planCaption: planCaption(household),
    active, basket, trip: { inBasket: trip.inBasket, total: trip.total }, chips, catalog, categoryMemory,
    tintFor, swipe, undo, announce,
    onAdd: (name, categoryId) => addWithUndo(name, categoryId),
    onAddFromBefore: entry => addWithUndo(entry.name, entry.categoryId),
    onOpenFromBefore: setDraft,
    onBrowse: () => setBrowsing(true),
    onPutBack: putBack,
    onEdit: openEditor,
    onDelete: deleteItem,
    onStartShopping: startShopping,
    shopLabel: tripRole === 'none' && basket.length > 0
      ? 'Continue shopping'
      : shopLabel(tripRole, joinName),
    onEndTrip: tripRole === 'join' ? askEnd : undefined,
    onChip: chip => onAdd(chip.name, chip.categoryId && byId.has(chip.categoryId) ? chip.categoryId : null),
    onUpgrade,
  }

  return (
    <>
      <ListReadyProvider>
        {layout === 'phone' ? <ShopView model={model} undoRef={undoRef} /> : <PlanView model={model} undoRef={undoRef} />}
      </ListReadyProvider>

      {/* Basket changes and trip results, read out politely (ux/04). */}
      <div className="ml-visually-hidden" aria-live="polite">{message}</div>

      <ItemEditor
        item={editingItem ?? draftItem}
        categories={categories}
        mode={mode}
        intent={draftItem && !editingItem ? 'add' : editing?.intent}
        tintFor={tintFor}
        onUpdate={(id, name, qty, cat, restore, note) => {
          if (draftItem && id === draftItem.id) addWithUndo(name, cat, qty, note)
          // Editing a basket item back onto the list is an add too.
          else if (restore && itemsLeft <= 0) onUpgrade('list_full')
          else onUpdate(id, name, qty, cat, restore, note)
        }}
        onPutInBasket={shoppingOpen ? id => { haptic(HAPTICS.drop); toBasket(id) } : undefined}
        zIndex={shoppingOpen ? 30 : undefined}
        onDelete={deleteItem}
        onClose={() => { setEditing(null); setDraft(null) }}
      />

      {shoppingOpen && shopping.trip && (
        <ShoppingMode
          ordered={ordered}
          categories={categories}
          storeName={store?.name ?? null}
          startedAt={shopping.trip.startedAt}
          inBasket={trip.inBasket}
          total={trip.total}
          basket={basket}
          swipe={shopSwipe}
          undo={undo}
          undoRef={shopUndoRef}
          tintFor={tintFor}
          onCheck={id => { haptic(HAPTICS.drop); shopSwipe.sendToBasket(id) }}
          onEdit={openEditor}
          onDelete={deleteItem}
          onPutBack={putBack}
          onFinish={finishTrip}
          onChangeStore={() => setPicker('change')}
          onLeave={() => setShoppingOpen(false)}
          others={shopping.others}
          onLeaveTrip={() => { shopping.leave(); setShoppingOpen(false) }}
        />
      )}

      {endAsking !== null && tripRole === 'join' && shopping.trip && (
        <EndTripDialog {...endTripText({ started_at: shopping.trip.startedAt }, basket, joinName, endAsking)}
          onEnd={endTrip} onKeep={() => setEndAsking(null)} />
      )}

      <StorePicker
        open={picker !== null}
        stores={shopping.stores}
        selected={shopping.suggestedStoreId}
        starting={picker === 'start'}
        onPick={pickStore}
        onAddStore={shopping.addStore}
        onClose={() => setPicker(null)}
      />

      <AllItemsSheet open={browsing} catalog={catalog} categories={categories} locale={userLocale()}
        tintFor={tintFor} onAdd={addAll} onClose={() => setBrowsing(false)} />

      <TripSheets sheet={finished && { summary: finished }} householdId={household.id} items={allItems}
        categories={categories} members={members} selfMemberId={selfMemberId} showSummary={prefs.tripSummary}
        onClose={() => setFinished(null)} />



    </>
  )
}

/**
 * A "From before" name as an editor row: not on the list until "Add to list".
 * The last amount stays a hint on the suggestion; it is never reused.
 */
function draftRow(entry: CatalogEntry, householdId: string): Item {
  return {
    id: `draft:${entry.key}`, household_id: householdId, name: entry.name,
    category_id: entry.categoryId, quantity: null, note: null,
    done: false, done_at: null, created_by: null, sort_order: 0,
    updated_at: '', created_at: '', deleted_at: null,
  }
}
